import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BedDouble, Bath, Maximize, ArrowLeft, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import axios from 'axios';
import Footer from '../components/sections/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PKG_TABS = [
  { key: 'standard', label: 'Standard 1.0' },
  { key: 'premium', label: 'Premium 2.0' },
  { key: 'luxury', label: 'Luxury 3.0' },
  { key: 'smart', label: 'Smart Luxury 4.0' },
];

const ProductDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isZh = i18n.language === 'zh';
  const [activeImg, setActiveImg] = useState(0);
  const [model, setModel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePkg, setActivePkg] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/admin/products`);
        const found = r.data.products.find(p => p.id === slug);
        if (found) setModel(found);
      } catch {}
      finally { setLoading(false); }
    };
    load();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-20"><div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" /></div>;

  if (!model) {
    return (
      <div className="min-h-screen flex items-center justify-center pt-20">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">{t('productDetail.notFound')}</h1>
          <Link to="/" className="text-red-600 hover:underline">{t('productDetail.backHome')}</Link>
        </div>
      </div>
    );
  }

  const images = model.images || [];
  const prevImg = () => setActiveImg(i => (i - 1 + images.length) % images.length);
  const nextImg = () => setActiveImg(i => (i + 1) % images.length);

  // Highlights: prefer rich text HTML, fallback to list
  const highlightsHtml = isZh ? model.highlights_html_zh : model.highlights_html_en;
  const highlightsList = isZh ? (model.highlights_zh || []) : (model.highlights_en || []);

  // Package images
  const pkgImages = model.package_images || {};

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <main className="flex-grow pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-6">
          <Button variant="outline" onClick={() => navigate(-1)} className="mb-8 border-gray-300 text-gray-600 hover:bg-gray-100 hover:text-gray-900" data-testid="back-button">
            <ArrowLeft className="w-4 h-4 mr-2" />{t('productDetail.backToModels')}
          </Button>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* LEFT — Image Gallery */}
            <div className="space-y-4" data-testid="product-gallery">
              <div className="relative rounded-xl overflow-hidden bg-gray-100 aspect-[4/3]">
                {images.length > 0 && <img src={images[activeImg]} alt={isZh ? model.title_zh : model.title_en} className="w-full h-full object-cover" data-testid="main-product-image" />}
                {images.length > 1 && (
                  <>
                    <button onClick={prevImg} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center shadow hover:bg-white transition" data-testid="gallery-prev"><ChevronLeft className="w-5 h-5 text-gray-700" /></button>
                    <button onClick={nextImg} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/80 backdrop-blur flex items-center justify-center shadow hover:bg-white transition" data-testid="gallery-next"><ChevronRight className="w-5 h-5 text-gray-700" /></button>
                    <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-3 py-1 rounded-full">{activeImg + 1} / {images.length}</div>
                  </>
                )}
              </div>
              <div className="flex gap-3">
                {images.map((img, idx) => (
                  <button key={idx} onClick={() => setActiveImg(idx)} className={`relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 transition-all ${idx === activeImg ? 'border-red-600 shadow-md' : 'border-transparent opacity-70 hover:opacity-100'}`} data-testid={`thumbnail-${idx}`}>
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* RIGHT — Product Info */}
            <div data-testid="product-info">
              <div className="flex justify-end gap-2 mb-4">
                {model.vr_link && (
                  <a href={model.vr_link} target="_blank" rel="noopener noreferrer">
                    <Button className="bg-red-600 hover:bg-red-700 text-white font-bold px-6 py-3 text-base rounded-lg" data-testid="vr-button">VR</Button>
                  </a>
                )}
                <Button onClick={() => navigate('/quote', { state: { bedrooms: model.bedrooms, bathrooms: model.bathrooms, square_footage: model.sqft } })}
                  className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 text-base rounded-lg shadow-lg" data-testid="generate-quote-btn">
                  {t('productDetail.generateQuote')}
                </Button>
              </div>

              <h1 className="text-2xl font-bold text-black mb-6 leading-tight" style={{ fontFamily: '"SimSun", "STSong", "Songti SC", serif' }} data-testid="product-title">
                {isZh ? model.title_zh : model.title_en}
              </h1>

              <div className="flex items-center gap-8 mb-8">
                <div className="flex items-center gap-2" data-testid="spec-bedrooms"><BedDouble className="w-6 h-6 text-red-600" /><span className="text-lg font-semibold text-gray-800">{model.bedrooms}</span></div>
                <div className="flex items-center gap-2" data-testid="spec-bathrooms"><Bath className="w-6 h-6 text-red-600" /><span className="text-lg font-semibold text-gray-800">{model.bathrooms}</span></div>
                <div className="flex items-center gap-2" data-testid="spec-sqft"><Maximize className="w-6 h-6 text-red-600" /><span className="text-lg font-semibold text-gray-800">{model.sqft} sq.ft</span></div>
              </div>

              {model.size_range && (
                <div className="mb-8 p-4 bg-gray-50 rounded-lg border border-gray-100">
                  <p className="text-sm text-gray-500 mb-1">{t('productDetail.sizeRange')}</p>
                  <p className="text-lg font-bold text-gray-900">{model.size_range} sq.ft</p>
                </div>
              )}

              <div className="mb-8">
                <h2 className="text-sm font-semibold text-gray-500 tracking-wider uppercase mb-3">{t('productDetail.description')}</h2>
                <p className="text-gray-700 leading-relaxed" data-testid="product-description">{isZh ? model.description_zh : model.description_en}</p>
              </div>

              {/* Highlights — rich text or list */}
              {(highlightsHtml || highlightsList.length > 0) && (
                <div className="mb-8">
                  <h2 className="text-sm font-semibold text-gray-500 tracking-wider uppercase mb-3">{t('productDetail.highlights')}</h2>
                  {highlightsHtml ? (
                    <div className="text-gray-700 leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: highlightsHtml }} />
                  ) : (
                    <ul className="space-y-3">
                      {highlightsList.map((h, i) => (
                        <li key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 rounded-full bg-red-600 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-white" /></div>
                          <span className="text-gray-700">{h}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Package Image Selector */}
              <div>
                <h2 className="text-sm font-semibold text-gray-500 tracking-wider uppercase mb-3">{isZh ? '套餐方案' : 'Packages'}</h2>
                <div className="flex flex-wrap gap-2 mb-4">
                  {PKG_TABS.map(pkg => (
                    <button key={pkg.key} onClick={() => setActivePkg(activePkg === pkg.key ? null : pkg.key)}
                      className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activePkg === pkg.key ? 'bg-red-600 text-white shadow-md' : 'bg-red-600 text-white opacity-70 hover:opacity-100'}`}
                      data-testid={`pkg-btn-${pkg.key}`}>
                      {pkg.label}
                    </button>
                  ))}
                </div>
                {activePkg && pkgImages[activePkg] && (
                  <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm">
                    <img src={pkgImages[activePkg]} alt={activePkg} className="w-full object-contain" data-testid={`pkg-img-${activePkg}`} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ProductDetail;
